import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Utils, authJsonHeaders, getLocalStorage, handleSessionExpired } from '../../utils';
import { MdSave, MdLock } from 'react-icons/md';

const GENERAL_SETTINGS_KEYS = [
    'site_name',
    'support_email',
    'tagline',
    'address',
    'phone',
];

// Billing-specific settings — kept in their own "Billing Settings" tab/table,
// separate from the general site settings above.
const BILLING_SETTINGS_KEYS = [
    'gst',
];

const SETTINGS_KEYS = [...GENERAL_SETTINGS_KEYS, ...BILLING_SETTINGS_KEYS];

const EMPTY_SETTINGS = Object.fromEntries(SETTINGS_KEYS.map((k) => [k, '']));

const InputField = ({ label, value, onChange, type = 'text', placeholder }) => (
    <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>{label}</label>
        <input
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            style={{
                width: '100%', padding: '12px', border: '1px solid #d1d5db',
                borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box'
            }}
        />
    </div>
);

const AdminSettings = () => {
    const [settings, setSettings] = useState(EMPTY_SETTINGS);
    const [tab, setTab] = useState('general');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
    const [passwordError, setPasswordError] = useState('');

    useEffect(() => {
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await fetch(Utils.baseurl + 'admin/settings', {
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                const newSettings = { ...EMPTY_SETTINGS };
                SETTINGS_KEYS.forEach((key) => {
                    if (result.data[key]?.value != null) newSettings[key] = result.data[key].value;
                });
                if (!newSettings.gst && result.data.gst?.value != null) {
                    newSettings.gst = result.data.gst.value;
                }
                setSettings(newSettings);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        }
        setLoading(false);
    };

    const saveSettingKeys = async (keys) => {
        setSaving(true);
        try {
            for (const key of keys) {
                const response = await fetch(Utils.baseurl + 'admin/settings/save', {
                    method: 'POST',
                    headers: authJsonHeaders(Utils.storageKeys.adminToken),
                    body: JSON.stringify({ key, value: settings[key] ?? '', type: 'text' })
                });
                const result = await response.json();
                if (handleSessionExpired(response, result)) return;
            }
            toast.success('Settings saved successfully!');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Error saving settings');
        }
        setSaving(false);
    };

    const handleSave = () => saveSettingKeys(GENERAL_SETTINGS_KEYS);
    const handleSaveBilling = () => saveSettingKeys(BILLING_SETTINGS_KEYS);

    const handlePasswordChange = async () => {
        setPasswordError('');
        if (passwordData.new !== passwordData.confirm) {
            setPasswordError('New passwords do not match');
            return;
        }
        if (passwordData.new.length < 6) {
            setPasswordError('Password must be at least 6 characters');
            return;
        }
        toast.info('Password change functionality would be implemented here');
        setPasswordData({ current: '', new: '', confirm: '' });
    };

    const adminData = JSON.parse(getLocalStorage(Utils.storageKeys.adminData) || '{}');

    const tabBtn = (id, label) => (
        <button
            type="button"
            onClick={() => setTab(id)}
            style={{
                padding: '10px 18px',
                border: 'none',
                borderBottom: tab === id ? '2px solid #4f46e5' : '2px solid transparent',
                background: 'transparent',
                color: tab === id ? '#4f46e5' : '#6b7280',
                fontWeight: tab === id ? '600' : '500',
                cursor: 'pointer',
                fontSize: '14px',
            }}
        >
            {label}
        </button>
    );

    if (loading) {
        return <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</div>;
    }

    return (
        <div>
            <div style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>Settings</h1>
                <p style={{ color: '#6b7280', marginTop: '8px' }}>Configure platform settings</p>
            </div>

            <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid #e5e7eb', marginBottom: '24px' }}>
                {tabBtn('general', 'General Settings')}
                {tabBtn('billing', 'Billing Settings')}
                {tabBtn('password', 'Change Password')}
                {tabBtn('admin', 'Admin Information')}
            </div>

            <div style={{ maxWidth: '640px' }}>
                {tab === 'general' && (
                    <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <InputField
                            label="Site Name"
                            value={settings.site_name}
                            onChange={(e) => setSettings((p) => ({ ...p, site_name: e.target.value }))}
                            placeholder="notifySphere"
                        />

                        <InputField
                            label="Support Email"
                            value={settings.support_email}
                            onChange={(e) => setSettings((p) => ({ ...p, support_email: e.target.value }))}
                            type="email"
                            placeholder="info@notifysphere.in"
                        />

                        <InputField
                            label="Tagline"
                            value={settings.tagline}
                            onChange={(e) => setSettings((p) => ({ ...p, tagline: e.target.value }))}
                            placeholder="Push Notifications & Marketing Platform"
                        />

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>Address</label>
                            <textarea
                                value={settings.address}
                                onChange={(e) => setSettings((p) => ({ ...p, address: e.target.value }))}
                                placeholder={'Company address line 1\nCity, State, PIN\nCountry'}
                                rows={4}
                                style={{
                                    width: '100%', padding: '12px', border: '1px solid #d1d5db',
                                    borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical'
                                }}
                            />
                        </div>

                        <InputField
                            label="Phone"
                            value={settings.phone}
                            onChange={(e) => setSettings((p) => ({ ...p, phone: e.target.value }))}
                            placeholder="+91 ..."
                        />

                        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#6b7280' }}>
                            Website on PDF uses APP_URL from server config.
                        </p>

                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '12px 24px', background: '#4f46e5', color: 'white',
                                border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer',
                                fontWeight: '500', opacity: saving ? 0.7 : 1
                            }}
                        >
                            <MdSave size={18} />
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                )}

                {tab === 'billing' && (
                    <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <InputField
                            label="GST % (on invoice subtotal)"
                            value={settings.gst}
                            onChange={(e) => setSettings((p) => ({ ...p, gst: e.target.value }))}
                            type="number"
                            placeholder="0"
                        />

                        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#6b7280' }}>
                            GST is calculated when an invoice is generated and stored on that invoice (a snapshot) — changing this later does not retroactively change already-generated invoices.
                        </p>

                        <button
                            type="button"
                            onClick={handleSaveBilling}
                            disabled={saving}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '12px 24px', background: '#4f46e5', color: 'white',
                                border: 'none', borderRadius: '8px', cursor: saving ? 'not-allowed' : 'pointer',
                                fontWeight: '500', opacity: saving ? 0.7 : 1
                            }}
                        >
                            <MdSave size={18} />
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                )}

                {tab === 'password' && (
                    <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        {passwordError && (
                            <div style={{ background: '#fef2f2', color: '#dc2626', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
                                {passwordError}
                            </div>
                        )}

                        <InputField
                            label="Current Password"
                            value={passwordData.current}
                            onChange={(e) => setPasswordData((p) => ({ ...p, current: e.target.value }))}
                            type="password"
                        />

                        <InputField
                            label="New Password"
                            value={passwordData.new}
                            onChange={(e) => setPasswordData((p) => ({ ...p, new: e.target.value }))}
                            type="password"
                        />

                        <InputField
                            label="Confirm New Password"
                            value={passwordData.confirm}
                            onChange={(e) => setPasswordData((p) => ({ ...p, confirm: e.target.value }))}
                            type="password"
                        />

                        <button
                            type="button"
                            onClick={handlePasswordChange}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '8px',
                                padding: '12px 24px', background: '#f59e0b', color: 'white',
                                border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500'
                            }}
                        >
                            <MdLock size={18} />
                            Change Password
                        </button>
                    </div>
                )}

                {tab === 'admin' && (
                    <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                        <div style={{ display: 'grid', gap: '12px' }}>
                            <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Name</div>
                                <div style={{ fontWeight: '500', color: '#1a1a1a' }}>{adminData.name || 'N/A'}</div>
                            </div>
                            <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Email</div>
                                <div style={{ fontWeight: '500', color: '#1a1a1a' }}>{adminData.email || 'N/A'}</div>
                            </div>
                            <div style={{ padding: '12px', background: '#f9fafb', borderRadius: '8px' }}>
                                <div style={{ fontSize: '12px', color: '#6b7280' }}>Role</div>
                                <div style={{ fontWeight: '500', color: '#1a1a1a', textTransform: 'capitalize' }}>{adminData.role || 'N/A'}</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminSettings;
